import React, { useEffect, useState } from "react";

import { paymentsService } from "api/services/payments.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";

import type { InstructorPaymentRecord, PaymentRecordStatus } from "api/types/payment.types";
import "pages/AdminPage.scss";

const STATUS_LABEL: Record<string, string> = {
  pending: "정산 대기",
  approved: "승인됨",
  paid: "정산 완료",
  cancelled: "취소됨",
};
const STATUS_BADGE: Record<string, "pending" | "info" | "active" | "rejected"> = {
  pending: "pending",
  approved: "info",
  paid: "active",
  cancelled: "rejected",
};

const PAGE_SIZE = 10;

const TABS: TabItem[] = [
  { key: "all", label: "전체" },
  { key: "pending", label: "정산 대기" },
  { key: "approved", label: "승인됨" },
  { key: "paid", label: "정산 완료" },
  { key: "cancelled", label: "취소됨" },
];

type PeriodKey = "1w" | "2w" | "1m" | "3m" | "custom";

const PERIODS: { key: PeriodKey; label: string; days: number }[] = [
  { key: "1w", label: "1주일", days: 7 },
  { key: "2w", label: "2주", days: 14 },
  { key: "1m", label: "1개월", days: 30 },
  { key: "3m", label: "3개월", days: 90 },
];

function toDateString(d: Date) {
  return d.toISOString().slice(0, 10);
}

const SettlementManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [activePeriod, setActivePeriod] = useState<PeriodKey | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [records, setRecords] = useState<InstructorPaymentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchRecords = async (page: number, status: string, from: string, to: string) => {
    setLoading(true);
    try {
      const res = await paymentsService.listRecords({
        page,
        page_size: PAGE_SIZE,
        ...(status !== "all" ? { status: status as PaymentRecordStatus } : {}),
        ...(from ? { session_date_from: from } : {}),
        ...(to ? { session_date_to: to } : {}),
      });
      setRecords(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(currentPage, activeTab, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab]);

  const applyPeriod = (key: PeriodKey) => {
    const period = PERIODS.find((p) => p.key === key);
    if (!period) return;
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - period.days);
    const fromStr = toDateString(from);
    const toStr = toDateString(to);
    setActivePeriod(key);
    setDateFrom(fromStr);
    setDateTo(toStr);
    setCurrentPage(1);
    fetchRecords(1, activeTab, fromStr, toStr);
  };

  const handleDateSearch = () => {
    setActivePeriod("custom");
    setCurrentPage(1);
    fetchRecords(1, activeTab, dateFrom, dateTo);
  };

  const handleApprove = async (id: number) => {
    await paymentsService.approveRecord(id);
    fetchRecords(currentPage, activeTab, dateFrom, dateTo);
  };

  const handlePay = async (id: number) => {
    await paymentsService.payRecord(id);
    fetchRecords(currentPage, activeTab, dateFrom, dateTo);
  };

  const totalAmount = records.reduce((s, r) => s + Number(r.total_amount), 0);
  const paidAmount = records.filter((r) => r.status === "paid").reduce((s, r) => s + Number(r.total_amount), 0);
  const pendingCount = records.filter((r) => r.status === "pending").length;

  const columns: Column<InstructorPaymentRecord>[] = [
    { key: "instructor_name", title: "강사명", width: "120px" },
    { key: "lesson_product_name", title: "레슨 상품", width: "200px" },
    { key: "session_date", title: "수업일", width: "120px" },
    { key: "total_students", title: "수강생 수", width: "90px", render: (v) => `${v}명` },
    { key: "attended_count", title: "출석", width: "70px", render: (v) => `${v}명` },
    {
      key: "total_amount",
      title: "정산 금액",
      width: "130px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    { key: "approved_by_name", title: "승인자", width: "110px", render: (v) => v || "-" },
    {
      key: "paid_at",
      title: "정산일",
      width: "120px",
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    {
      key: "status",
      title: "상태",
      width: "100px",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{STATUS_LABEL[String(v)] ?? String(v)}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "160px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 6 }}>
          {row.status === "pending" && (
            <Button size="sm" variant="primary" onClick={() => handleApprove(row.id)}>
              승인
            </Button>
          )}
          {row.status === "approved" && (
            <Button size="sm" variant="primary" onClick={() => handlePay(row.id)}>
              정산 처리
            </Button>
          )}
          <Button size="sm" variant="ghost">
            상세
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="정산 관리"
        description="강사 정산 내역을 조회하고 처리합니다."
        actions={[
          <Button key="export" variant="outline">
            엑셀 다운로드
          </Button>,
        ]}
      />

      <div className="stat-cards">
        <div className="stat-card stat-card--primary">
          <div className="stat-card__label">총 정산금액 (현재 조회)</div>
          <div className="stat-card__value">{totalAmount.toLocaleString()}원</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__label">정산 완료</div>
          <div className="stat-card__value">{paidAmount.toLocaleString()}원</div>
        </div>
        <div className="stat-card stat-card--warning">
          <div className="stat-card__label">정산 대기</div>
          <div className="stat-card__value">{pendingCount}건</div>
        </div>
      </div>

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />

        <div className="admin-page__filter" style={{ flexWrap: "wrap", gap: 8 }}>
          {/* Period quick-select buttons */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#71717A", marginRight: 4 }}>기간</span>
            {PERIODS.map((p) => (
              <Button key={p.key} size="sm" variant={activePeriod === p.key ? "primary" : "outline"} onClick={() => applyPeriod(p.key)}>
                {p.label}
              </Button>
            ))}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setActivePeriod("custom");
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <span style={{ color: "#71717A" }}>~</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setActivePeriod("custom");
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <Button variant="secondary" size="sm" onClick={handleDateSearch}>
              조회
            </Button>
          </div>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={records} rowKey="id" />}
        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={setCurrentPage} />
      </div>
    </div>
  );
};

export default SettlementManagement;
